import React, { useState } from 'react';
import './IngestOptionsDialog.css';

/**
 * Lightweight dialog shown when user clicks "Ingest" on a group card.
 *
 * Props:
 *   isOpen       - boolean
 *   groupName    - display name of the group
 *   sourceType   - ingestion config type (e.g. google_drive_folder, google_spreadsheet)
 *   hasLinkFields - whether the group's column_mapping has any link:true fields
 *   onClose      - () => void
 *   onConfirm    - ({ startRow, totalCount, validateLinks }) => void
 */
export default function IngestOptionsDialog({
  isOpen,
  groupName,
  sourceType,
  hasLinkFields,
  onClose,
  onConfirm,
}) {
  const [startRow, setStartRow] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const isDrive = sourceType === 'google_drive_folder';
  const isApi = sourceType === 'api_source';

  if (!isOpen) return null;

  const handleConfirm = () => {
    const payload = {
      validateLinks: true,
    };
    if (!isDrive && !isApi) {
      payload.startRow = Math.max(0, startRow);
      payload.totalCount = Math.max(0, totalCount);
    }
    onConfirm(payload);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') onClose();
    if (e.key === 'Enter') handleConfirm();
  };

  return (
    <div className="ingest-options-overlay" onKeyDown={handleKeyDown}>
      <div className="ingest-options-dialog">
        <div className="ingest-options-header">
          <h3><i className="fas fa-file-import"></i> Ingest: {groupName}</h3>
          <button className="ingest-options-close-btn" onClick={onClose}>&times;</button>
        </div>
        <div className="ingest-options-body">
          {isApi && (
            <div className="ingest-options-drive">
              <p className="ingest-options-hint">
                Poll the configured API once and import the returned records into this group.
              </p>
              <p className="ingest-options-hint ingest-options-hint--muted">
                Provider settings live in Settings → Ingestion (edit source) and System Settings (API_PROVIDER_BLOB / credentials).
              </p>
            </div>
          )}

          {!isDrive && !isApi && (
            <>
              <div className="ingest-options-hint">
                <code>0, 0</code> — ingest the whole document.<br />
                <code>1, 1</code> — easy test (one row).<br />
                <code>20, 0</code> — start at row 20, process the rest.<br />
                <code>20, 10</code> — do up to 10 rows starting from row 20.
              </div>
              <div className="ingest-options-row">
                <div className="ingest-options-field">
                  <label htmlFor="ingest-start-row">Starting Row</label>
                  <input
                    id="ingest-start-row"
                    type="number"
                    min="0"
                    value={startRow}
                    onChange={(e) => setStartRow(parseInt(e.target.value, 10) || 0)}
                    autoFocus
                  />
                </div>
                <div className="ingest-options-field">
                  <label htmlFor="ingest-total-count">Total Count</label>
                  <input
                    id="ingest-total-count"
                    type="number"
                    min="0"
                    value={totalCount}
                    onChange={(e) => setTotalCount(parseInt(e.target.value, 10) || 0)}
                  />
                </div>
              </div>
            </>
          )}

          {isDrive && (
            <div className="ingest-options-drive">
              <p className="ingest-options-hint">
                Google Drive folder ingest runs on demand. Files are downloaded and stored for this group.
              </p>
              <p className="ingest-options-hint ingest-options-hint--muted">
                Directory depth columns and other Drive options are configured in Settings → Ingestion when you create or edit this group.
              </p>
            </div>
          )}

          {hasLinkFields && (
            <div className="ingest-options-link-notice">
              <i className="fas fa-link"></i>
              <span>Link fields detected — all links will be validated before ingestion. If any link cannot be opened or has no content, the entire ingestion will be aborted.</span>
            </div>
          )}
        </div>
        <div className="ingest-options-footer">
          <button className="btn btn-cancel" onClick={onClose}>Cancel</button>
          <button className="btn btn-ingest-confirm" onClick={handleConfirm}>
            <i className="fas fa-play"></i> Ingest
          </button>
        </div>
      </div>
    </div>
  );
}
